import Controller, { inject as controller } from '@ember/controller';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { timeout } from 'ember-concurrency';
import AccountService from 'cio/services/account';
import SignupService from 'cio/services/signup';
import config from 'cio/config/environment';
import fade from 'ember-animated/transitions/fade';
import EnvironmentService from 'cio/services/environment';
import RouterService from '@ember/routing/router-service';
import AccountModel from 'cio/models/account';
import DS from 'ember-data';
import ApiService from 'cio/services/api';
import { ProductApp } from 'cio/utils/product-app-info';
import HydraService from 'cio/services/hydra';
import SignupController from 'cio/pods/signup/controller';
import SetupGuideModel from 'cio/models/setup-guide';
import { MessagingChannelType } from 'cio/utils/setup-guide/messaging-channels';

interface HydraWorkspace {
  id: number;
  account_id: number;
  cio_workspace_id: number;
  cdp_workspace_id: number;
  cio_initialized: boolean;
  cdp_initialized: boolean;
  name: string;
  color: string;
  cio_workspace_meta: {
    object_types: { id: number; name: string; slug: string; colour: string; icon: string }[];
  };
}

export default class PostRegistrationController extends Controller {
  @service api!: ApiService;
  @service signup!: SignupService;
  @service account!: AccountService;
  @service hydra!: HydraService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;
  @service router!: RouterService;

  logoColorHorizontalWhiteImage = new URL(
    'cio/assets/images/logo-color-horizontal-white.svg',
    import.meta.url,
  ).toString();
  logoColorImage = new URL('cio/assets/images/logo-color.svg', import.meta.url).toString();

  fade = fade;

  @controller('signup') signupController!: SignupController;

  get currentRouteName() {
    return this.router.currentRouteName;
  }

  waitForAccountToBeReadyTask = task(async () => {
    let start = window.performance.now();
    // Right after the account is created, the environment tables may not be
    // ready. So we poll for the environment and allow visiting the dashboard
    // only when the tables are known to be created, i.e. we have received a
    // non-4xx non-5xx response from the server.
    //
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        // Need to load the account first because we need to get the
        // productActivations state
        let account: AccountModel = await this.store.queryRecord('account', {});

        if (this.hydra.isHydraEnabled) {
          let workspacesResponse = await this.api.request<HydraWorkspace[]>('workspaces', {
            pathScope: 'hydra_account',
          });

          // On creation there's only one workspace, so we get the first one
          // from the list
          if (workspacesResponse.length === 0) {
            throw new Error('No workspaces found');
          }

          let [defaultWorkspace] = workspacesResponse;

          let isWorkspaceInitialized = false;
          if (account.productActivations.includes(ProductApp.DATA_PIPELINES)) {
            isWorkspaceInitialized = defaultWorkspace.cdp_initialized;
          } else {
            isWorkspaceInitialized = defaultWorkspace.cio_initialized;
          }

          if (!isWorkspaceInitialized) {
            // Throw an error to keep polling.
            throw new Error('Workspace is not initialized');
          }

          // Set the environment id, so that the application adapter knows what
          // ID to use when making requests
          this.environment.set('current', {
            ...this.environment.current,
            id: defaultWorkspace.id,
            fakePostSignup: true,
          });
        } else {
          let environment = await this.store.findRecord('environment', account.currentUser.defaultEnvironment?.id);

          this.environment.set('current', environment);
        }
        break;
      } catch {
        await timeout(config.APP.signupPollingDelay);
      }
    }

    let elapsedTime = window.performance.now() - start;

    return { elapsedTime };
  });

  updateSetupGuideTask = task(async (property: string, value: string | boolean | number) => {
    // Wait for the account to be ready before updating the setup guide,
    // as it requires an enviroment ID to be set.
    // This task is called by the post-registration route's activate hook,
    // so it should be running/have run by the time this task is called,
    // which is why we don't need to `perform` it here.
    if (this.waitForAccountToBeReadyTask.isRunning) {
      await this.waitForAccountToBeReadyTask.last;
    }
    let setupGuide = await this.store.findRecord('setup-guide', 'current');
    await setupGuide.set(property as keyof SetupGuideModel, value);
    return setupGuide.save();
  });

  updateSetupGuideMessagingChannelsTask = task(async (allSelectedChannels: string[]) => {
    let setupGuide = await this.store.findRecord('setup-guide', 'current');
    await setupGuide.addChannels(allSelectedChannels as MessagingChannelType[]);
    return setupGuide.save();
  });
}

declare module '@ember/controller' {
  interface Registry {
    'authenticated/post-registration': PostRegistrationController;
  }
}
